import React, { useState, useRef } from 'react';
import { Save, Upload, FileText, File } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ComplaintsPolicyTab = ({ complaintsPolicy, setComplaintsPolicy, saveComplaintsPolicy, getStoredPassword, fetchAllData }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragging(false); };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) await uploadFile(e.dataTransfer.files[0]);
  };

  const handleFileSelect = async (e) => {
    if (e.target.files.length > 0) await uploadFile(e.target.files[0]);
  };

  const uploadFile = async (file) => {
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please upload a PDF file');
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await fetch(
        `${API_URL}/api/upload/complaints-policy?password=${getStoredPassword()}`,
        { method: 'POST', body: formData }
      );
      if (response.ok) {
        const data = await response.json();
        setComplaintsPolicy({ pdf_url: data.pdf_url, pdf_filename: data.pdf_filename });
        toast.success('PDF uploaded successfully!');
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Upload failed');
      }
    } catch (error) {
      toast.error('Failed to upload file');
    }
    setUploading(false);
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg text-white mb-6">Complaints Policy PDF</h3>
      <p className="text-white/50 text-sm mb-4">
        Upload your company's complaints policy PDF. This will be displayed on the Complaints Procedure page 
        for visitors to download.
      </p>
      
      {/* Drag & Drop Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`
          relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-300
          ${isDragging 
            ? 'border-[#D4AF37] bg-[#D4AF37]/10 scale-[1.02]' 
            : 'border-white/20 hover:border-[#D4AF37]/50 bg-white/[0.02] hover:bg-white/[0.04]'
          }
          ${uploading ? 'pointer-events-none opacity-60' : ''}
        `}
      >
        <input ref={fileInputRef} type="file" accept=".pdf" onChange={handleFileSelect} className="hidden" />
        <div className="flex flex-col items-center gap-4">
          {uploading ? (
            <>
              <div className="w-16 h-16 border-4 border-[#D4AF37]/30 border-t-[#D4AF37] rounded-full animate-spin"></div>
              <p className="text-[#D4AF37]">Uploading...</p>
            </>
          ) : (
            <>
              <div className={`w-16 h-16 rounded-full flex items-center justify-center transition-colors ${
                isDragging ? 'bg-[#D4AF37]/20' : 'bg-white/5'
              }`}>
                <Upload className={`w-8 h-8 ${isDragging ? 'text-[#D4AF37]' : 'text-white/40'}`} />
              </div>
              <div>
                <p className="text-white font-medium mb-1">
                  {isDragging ? 'Drop your PDF here' : 'Drag & Drop your PDF here'}
                </p>
                <p className="text-white/40 text-sm">or click to browse files</p>
              </div>
              <div className="flex items-center gap-2 text-white/30 text-xs">
                <File className="w-4 h-4" />
                <span>PDF files only (max 10MB)</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Current File Status */}
      {complaintsPolicy.pdf_url && (
        <div className="p-4 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#D4AF37]/20 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <p className="text-white font-medium text-sm">{complaintsPolicy.pdf_filename || 'Complaints Policy'}</p>
                <p className="text-[#D4AF37] text-xs">Active - visible on complaints page</p>
              </div>
            </div>
            <a 
              href={`${API_URL}${complaintsPolicy.pdf_url}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#D4AF37] hover:text-white text-sm underline"
            >
              View PDF
            </a>
          </div>
        </div>
      )}

      {/* Divider */}
      <div className="relative py-4">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-white/10"></div>
        </div>
        <div className="relative flex justify-center">
          <span className="bg-[#0a1a14] px-4 text-white/30 text-sm">or enter URL manually</span>
        </div>
      </div>

      {/* Manual URL Input */}
      <div className="p-6 bg-white/5 border border-white/10 rounded-lg">
        <div className="space-y-4">
          <div>
            <label className="text-white/50 text-sm block mb-2">PDF URL (External Link)</label>
            <Input
              value={complaintsPolicy.pdf_url}
              onChange={(e) => setComplaintsPolicy({...complaintsPolicy, pdf_url: e.target.value})}
              className="bg-white/5 border-white/10 text-white"
              placeholder="https://drive.google.com/file/d/..."
            />
          </div>
          <div>
            <label className="text-white/50 text-sm block mb-2">Display Filename</label>
            <Input
              value={complaintsPolicy.pdf_filename}
              onChange={(e) => setComplaintsPolicy({...complaintsPolicy, pdf_filename: e.target.value})}
              className="bg-white/5 border-white/10 text-white"
              placeholder="BlackStarPI-Complaints-Policy.pdf"
            />
          </div>
        </div>
      </div>
      
      <Button onClick={saveComplaintsPolicy} className="bg-[#D4AF37] text-black hover:bg-[#B8941E]" data-testid="save-complaints-btn">
        <Save className="w-4 h-4 mr-2" /> Save Changes
      </Button>
    </div>
  );
};
